import { useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline'
import ScienceIcon from '@mui/icons-material/ScienceOutlined'
import { ApiError, api } from '../../api/client.js'

/**
 * Le canal WhatsApp de la boutique.
 *
 * ⭐ Ces identifiants vivent dans le SCHÉMA DE LA BOUTIQUE, pas dans la
 * plateforme : c'est son numéro d'expéditeur, son compte Meta, sa facture.
 * Deux enseignes du même groupe n'envoient pas depuis le même numéro.
 *
 * ⚠️ Le JETON ne revient JAMAIS du serveur. Le champ reste vide même quand un
 * jeton est enregistré, et le laisser vide le CONSERVE — c'est ce qui permet
 * de corriger le nom du modèle sans avoir à ressaisir un secret de 200
 * caractères, et ce qui garantit qu'il ne traîne pas dans une réponse HTTP.
 */

interface ReglagesLus {
  configure: boolean
  phoneNumberId: string | null
  templateName: string | null
  templateLang: string
  fuseau?: string
  pays?: string | null
}

export function ReglagesWhatsApp({ storeId }: { storeId: string }): JSX.Element {
  const [lus, setLus] = useState<ReglagesLus | null>(null)
  const [phoneNumberId, setPhoneNumberId] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [templateLang, setTemplateLang] = useState('fr')
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<ReglagesLus>(`/stores/${storeId}/campaigns/reglages`)
      .then((r) => {
        setLus(r)
        setPhoneNumberId(r.phoneNumberId ?? '')
        setTemplateName(r.templateName ?? '')
        setTemplateLang(r.templateLang || 'fr')
      })
      .catch((e: unknown) =>
        setErreur(e instanceof ApiError ? e.message : 'Réglages illisibles.'),
      )
  }, [storeId])

  async function enregistrer(): Promise<void> {
    setOccupe(true)
    setErreur(null)
    setSucces(null)
    try {
      const r = await api.put<ReglagesLus>(`/stores/${storeId}/campaigns/reglages`, {
        phoneNumberId: phoneNumberId.trim() || undefined,
        // Vide = on garde celui qui est enregistré (voir l'entête).
        accessToken: accessToken.trim() || undefined,
        templateName: templateName.trim() || undefined,
        templateLang: templateLang.trim() || 'fr',
      })
      setLus((l) => ({ ...(l ?? { templateLang: 'fr' }), ...r }))
      setAccessToken('')
      setSucces('Réglages enregistrés.')
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Enregistrement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  if (!lus && !erreur) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 720 }}>
      {erreur && (
        <Alert severity="error" onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}
      {succes && (
        <Alert severity="success" onClose={() => setSucces(null)}>
          {succes}
        </Alert>
      )}

      {/**
       * ⚠️ Cette explication n'est pas décorative. Sans modèle approuvé, les
       * envois partent et reviennent en échec un par un : autant que ce soit
       * lu ici, avant, plutôt que découvert dans un rapport à 400 lignes.
       */}
      <Alert severity="info">
        <AlertTitle>WhatsApp n’est pas un SMS</AlertTitle>
        Meta ne remet un message publicitaire que par un <strong>modèle approuvé</strong> par ses
        soins — ou en texte libre dans les 24 h suivant un message du client, ce qui ne couvre
        jamais une campagne. Cette règle appartient à l’opérateur : aucun réglage ici ne la
        contourne. Créez et faites valider votre modèle dans le gestionnaire WhatsApp de Meta, puis
        reportez son nom ci-dessous.
      </Alert>

      <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
          {lus?.configure ? (
            <>
              <CheckCircleIcon sx={{ color: 'success.main' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Canal réel configuré
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Les campagnes partent pour de bon.
                  {!lus.templateName && ' ⚠️ Aucun modèle renseigné : les envois échoueront.'}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <ScienceIcon sx={{ color: 'info.main' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Mode simulation
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Tout fonctionne — segments, préparation, rapports — mais aucun message ne part
                  réellement.
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* ⚠️ La confusion que tout le monde fait : coller ici le numéro de
              téléphone. Meta attend son identifiant interne, en chiffres. Le
              dire au moment de la saisie évite un 404 de l'API Graph une heure
              plus tard, sans rien pour en désigner la cause. */}
          <TextField
            size="small"
            label="Identifiant Meta du numéro expéditeur"
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value)}
            error={phoneNumberId.trim() !== '' && !/^\d{5,}$/.test(phoneNumberId.trim())}
            helperText={
              phoneNumberId.trim() !== '' && !/^\d{5,}$/.test(phoneNumberId.trim())
                ? 'Ce n’est pas le numéro de téléphone : Meta attribue au numéro un identifiant interne, en chiffres uniquement (ex. 109876543210987).'
                : '« Phone number ID » dans la console Meta — des chiffres, pas le numéro lui-même.'
            }
          />

          <TextField
            size="small"
            type="password"
            label="Jeton d’accès permanent"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            placeholder={lus?.configure ? '•••••••• (enregistré)' : ''}
            autoComplete="off"
            helperText={
              lus?.configure
                ? 'Un jeton est enregistré. Laissez vide pour le conserver, saisissez-en un pour le remplacer.'
                : 'Il n’est jamais réaffiché après enregistrement.'
            }
          />

          <TextField
            size="small"
            label="Nom du modèle approuvé"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            helperText="Le modèle validé par Meta. Le texte de votre campagne en devient le paramètre."
          />

          <TextField
            size="small"
            label="Langue du modèle"
            value={templateLang}
            onChange={(e) => setTemplateLang(e.target.value)}
            sx={{ maxWidth: 200 }}
            helperText="« fr », « fr_FR »… telle qu’elle figure chez Meta."
          />

          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Button variant="contained" disabled={occupe} onClick={() => void enregistrer()}>
              {occupe ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            {lus?.pays && (
              <Chip
                size="small"
                variant="outlined"
                label={`Numéros mis en forme pour ${lus.pays} · fuseau ${lus.fuseau ?? '—'}`}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Ces identifiants appartiennent à cette boutique seule. Ils ne sont lisibles par aucune autre
        enseigne, et le jeton n’est renvoyé à aucun écran — voir{' '}
        <Link
          href="https://developers.facebook.com/docs/whatsapp/cloud-api"
          target="_blank"
          rel="noreferrer noopener"
        >
          la documentation de l’API Cloud
        </Link>{' '}
        pour les obtenir.
      </Typography>
    </Box>
  )
}
